// src/services/api/subscriptions.api.ts
// Запросы раздела «Абонементы» (годовое членство клуба).
import { apiRequest } from "./client";
import {
    AdminCreateSubscriptionPayload,
    ListSubscriptionsFilters,
    SubscriptionItem,
    SubscriptionSendResult,
    SubscriptionsPageResponse,
    UpdateSubscriptionPayload,
} from "../types/subscriptions.types";

export const getSubscriptionsRequest = async (
    page: number,
    pageSize: number,
    filters: ListSubscriptionsFilters,
): Promise<SubscriptionsPageResponse> => {
    const params = new URLSearchParams();

    params.set("page", String(page));
    params.set("page_size", String(pageSize));

    if (filters.search?.trim()) {
        params.set("search", filters.search.trim());
    }

    if (filters.status) {
        params.set("status", filters.status);
    }

    if (filters.include_deleted) {
        params.set("include_deleted", "true");
    }

    // Даты передаём как календарные дни: сервер сам берёт весь день по Берлину.
    if (filters.date_from) {
        params.set("date_from", filters.date_from);
    }

    if (filters.date_to) {
        params.set("date_to", filters.date_to);
    }

    if (filters.site_source) {
        params.set("site_source", filters.site_source);
    }

    return apiRequest<SubscriptionsPageResponse>(
        `/subscriptions?${params.toString()}`,
        { method: "GET", auth: true },
    );
};

export const getSubscriptionRequest = (subscriptionId: string) =>
    apiRequest<SubscriptionItem>(`/subscriptions/${subscriptionId}`, {
        method: "GET",
        auth: true,
    });

/**
 * Выдача абонемента админом по email клиента.
 * Сумма 0 — абонемент активируется сразу и клиенту уходит поздравление
 * с датой завершения вместо счёта.
 */
export const adminCreateSubscriptionRequest = (
    payload: AdminCreateSubscriptionPayload,
) =>
    apiRequest<SubscriptionItem>("/subscriptions/admin", {
        method: "POST",
        auth: true,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });

/** is_paid: true — отметка оплаты по счёту, включает абонемент на год. */
export const updateSubscriptionRequest = (
    subscriptionId: string,
    payload: UpdateSubscriptionPayload,
) =>
    apiRequest<SubscriptionItem>(`/subscriptions/${subscriptionId}`, {
        method: "PATCH",
        auth: true,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });

/**
 * Аннулирование скидки: дата завершения переписывается на текущий момент.
 * Абонемент остаётся в списке со статусом «истекло».
 */
export const annulSubscriptionRequest = (subscriptionId: string) =>
    apiRequest<SubscriptionItem>(`/subscriptions/${subscriptionId}/annul`, {
        method: "POST",
        auth: true,
    });

export const deleteSubscriptionRequest = (subscriptionId: string) =>
    apiRequest<SubscriptionItem>(`/subscriptions/${subscriptionId}`, {
        method: "DELETE",
        auth: true,
    });

/** Отправка счёта на почту. Для оплаченного абонемента уходит поздравление. */
export const sendSubscriptionInvoiceRequest = (subscriptionId: string) =>
    apiRequest<SubscriptionSendResult>(
        `/subscriptions/${subscriptionId}/send-invoice`,
        { method: "POST", auth: true },
    );

/** Письмо с кнопкой «оплатить», ведущей сразу на страницу оплаты Stripe. */
export const sendSubscriptionPaymentLinkRequest = (subscriptionId: string) =>
    apiRequest<SubscriptionSendResult>(
        `/subscriptions/${subscriptionId}/send-payment-link`,
        { method: "POST", auth: true },
    );

/**
 * Скачивание PDF счёта. apiRequest не умеет возвращать blob,
 * поэтому здесь используется fetch напрямую — как в orders.api.ts.
 */
export const downloadSubscriptionInvoicePdfRequest = async (
    subscriptionId: string,
): Promise<{ blob: Blob; filename: string }> => {
    const { API_BASE_URL } = await import("../config/env");
    const { getAccessToken } = await import("../storage/token.storage");

    const token = getAccessToken();
    const response = await fetch(
        `${API_BASE_URL}/subscriptions/${subscriptionId}/invoice.pdf`,
        {
            method: "GET",
            headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
    );

    if (!response.ok) {
        let detail = `HTTP ${response.status}`;
        try {
            const data = await response.json();
            if (data?.detail) detail = String(data.detail);
        } catch {
            // ignore
        }
        throw new Error(detail);
    }

    const blob = await response.blob();

    let filename = `abo-rechnung-${subscriptionId}.pdf`;
    const cd = response.headers.get("content-disposition");
    if (cd) {
        const m = cd.match(/filename\*?=(?:UTF-8'')?\"?([^\";]+)\"?/i);
        if (m && m[1]) filename = decodeURIComponent(m[1]);
    }

    return { blob, filename };
};
